import { Add, Check, EventAvailable } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocaleState, useNotify, useTranslate } from "react-admin";

import {
  ActivityRecommendationsQuery,
  PatientProceduresDocument,
  PatientProceduresQuery,
  PatientProceduresQueryVariables,
  PlanRecommendedActivityDocument,
  PlanRecommendedActivityMutation,
  PlanRecommendedActivityMutationVariables,
  ProcedureStatusEnum,
} from "../generated/graphql";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { client } from "../lib/apolloClient";
import { formatDateTimeInline } from "../lib/dateUtils";
import { sortProceduresByPlannedStart } from "../providers/procedureSort";
import { borderRadius, color, typography } from "../theme/designTokens";
import { ActivityCategoryIcon } from "./ActivityCategoryIcon";
import { ActivityGoalKind } from "./activityGoalKinds";

type Recommendation = ActivityRecommendationsQuery["activityRecommendations"][number];

type ProcedureOption = NonNullable<
  NonNullable<PatientProceduresQuery["procedureParticipants"]>["nodes"][number]["procedure"]
>;

type PlanMode = "existing" | "new";

enum ProcedureLoadStatus {
  Loaded = "loaded",
  Error = "error",
}

// Default planned start: the next full hour, formatted for a datetime-local input.
const defaultPlannedStart = (): string => {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1);
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export const PlanActivityDialog = ({
  open,
  onClose,
  onPlanned,
  recommendation,
  kind,
  userId,
  patientId,
  canCreateNewProcedure,
}: {
  open: boolean;
  onClose: () => void;
  onPlanned: () => void;
  recommendation: Recommendation;
  kind: ActivityGoalKind;
  userId: string;
  patientId: string;
  canCreateNewProcedure: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();
  const [locale] = useLocaleState();

  const [procedures, setProcedures] = useState<ProcedureOption[]>([]);
  const [mode, setMode] = useState<PlanMode>(canCreateNewProcedure ? "new" : "existing");
  const [procedureId, setProcedureId] = useState<string>("");
  const [title, setTitle] = useState(recommendation.title);
  const [plannedStart, setPlannedStart] = useState(defaultPlannedStart);
  const [isSaving, setIsSaving] = useState(false);
  const [procedureLoadStatus, setProcedureLoadStatus] = useState(ProcedureLoadStatus.Loaded);

  useEffect(() => {
    let active = true;
    setProcedures([]);
    setProcedureId("");
    setMode(canCreateNewProcedure ? "new" : "existing");
    setProcedureLoadStatus(ProcedureLoadStatus.Loaded);
    void (async () => {
      try {
        const result = await client.query<PatientProceduresQuery, PatientProceduresQueryVariables>({
          query: PatientProceduresDocument,
          variables: { patientId },
          fetchPolicy: "network-only",
        });
        if (!active) return;
        const options = sortProceduresByPlannedStart(
          (result.data.procedureParticipants?.nodes ?? [])
            .map(node => node.procedure)
            .filter(
              (procedure): procedure is ProcedureOption =>
                procedure != null && procedure.status === ProcedureStatusEnum.Preparation
            ),
          false
        );
        setProcedures(options);
        if (options.length > 0) {
          setMode("existing");
          setProcedureId(options[0].id);
        }
      } catch (error) {
        if (!active) return;
        handleError(error, translate("activityGoals.plan.proceduresLoadError"), {
          operation: "patientProcedures",
          patientId,
        });
        setProcedureLoadStatus(ProcedureLoadStatus.Error);
      }
    })();
    return () => {
      active = false;
    };
  }, [canCreateNewProcedure, handleError, patientId, translate]);

  const canSave = useMemo(() => {
    if (isSaving) return false;
    if (mode === "existing") return procedureId !== "";
    if (!canCreateNewProcedure) return false;
    return title.trim() !== "" && plannedStart !== "";
  }, [canCreateNewProcedure, isSaving, mode, procedureId, title, plannedStart]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const contentRef = {
        userId,
        contentUuid: recommendation.contentUuid,
      };
      const input: PlanRecommendedActivityMutationVariables["input"] =
        mode === "existing"
          ? { ...contentRef, procedureId }
          : {
              ...contentRef,
              newProcedure: { title: title.trim(), plannedStart: new Date(plannedStart).toISOString() },
            };

      const result = await client.mutate<PlanRecommendedActivityMutation, PlanRecommendedActivityMutationVariables>({
        mutation: PlanRecommendedActivityDocument,
        variables: { input },
      });

      if (!result.data?.planRecommendedActivity?.procedureId) {
        throw new Error("planRecommendedActivity returned no procedure");
      }

      notify("activityGoals.plan.success", { type: "success" });
      onPlanned();
      onClose();
    } catch (error) {
      handleError(error, translate("activityGoals.plan.error"));
    } finally {
      setIsSaving(false);
    }
  };

  const sectionLabelSx = {
    fontSize: typography.fontSize.xxs,
    fontWeight: 600,
    color: color.gray,
    letterSpacing: "0.2px",
    mb: 1,
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { maxWidth: 420, backgroundColor: color.white } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: typography.fontSize.md, fontWeight: 700 }}>
          {translate("activityGoals.plan.title")}
        </Typography>
        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
          <ActivityCategoryIcon kind={kind} size={26} />
          <Typography variant="body2" sx={{ color: color.gray }}>
            {recommendation.title} ·{" "}
            {translate("activityGoals.recommendations.duration", {
              minutes: recommendation.durationMinutes,
            })}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2 }}>
        {procedureLoadStatus === ProcedureLoadStatus.Error && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="plan-procedures-load-error">
            {translate("activityGoals.plan.proceduresLoadError")}
          </Alert>
        )}

        {procedures.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={sectionLabelSx}>{translate("activityGoals.plan.existingProcedure")}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.875, maxHeight: 230, overflowY: "auto" }}>
              {procedures.map(procedure => {
                const selected = mode === "existing" && procedureId === procedure.id;
                return (
                  <Box
                    component="button"
                    type="button"
                    key={procedure.id}
                    onClick={() => {
                      setMode("existing");
                      setProcedureId(procedure.id);
                    }}
                    data-testid={`plan-procedure-${procedure.id}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      textAlign: "left",
                      width: "100%",
                      cursor: "pointer",
                      border: `1px solid ${selected ? color.primary : color.datagridBorder}`,
                      backgroundColor: selected ? color.selectedOptionBackground : color.white,
                      borderRadius: borderRadius.md,
                      p: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        flex: "0 0 20px",
                        borderRadius: borderRadius.sm,
                        border: `2px solid ${selected ? color.primary : color.selectedOptionBorder}`,
                        backgroundColor: selected ? color.primary : color.white,
                        display: "grid",
                        placeItems: "center",
                        color: color.white,
                      }}
                    >
                      {selected && <Check sx={{ fontSize: typography.fontSize.sm }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {procedure.codeDisplay ?? translate("activityGoals.plan.untitledProcedure")}
                      </Typography>
                      {procedure.plannedStart && (
                        <Typography variant="caption" sx={{ color: color.gray }}>
                          {formatDateTimeInline(procedure.plannedStart, locale)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {canCreateNewProcedure &&
          (mode === "new" ? (
            <Box>
              <Typography sx={sectionLabelSx}>{translate("activityGoals.plan.newProcedure")}</Typography>
              <TextField
                fullWidth
                size="small"
                label={translate("activityGoals.plan.newTitleLabel")}
                value={title}
                onChange={event => setTitle(event.target.value)}
                data-testid="plan-new-title"
              />
              <TextField
                fullWidth
                size="small"
                margin="normal"
                type="datetime-local"
                label={translate("activityGoals.plan.plannedStartLabel")}
                value={plannedStart}
                onChange={event => setPlannedStart(event.target.value)}
                InputLabelProps={{ shrink: true }}
                data-testid="plan-new-planned-start"
              />
            </Box>
          ) : (
            <Button
              fullWidth
              startIcon={<Add sx={{ fontSize: typography.fontSize.md }} />}
              onClick={() => setMode("new")}
              data-testid="plan-mode-new"
              sx={{
                color: color.secondary,
                border: `1px dashed ${color.datagridBorder}`,
                borderRadius: borderRadius.md,
                py: 1.25,
              }}
            >
              {translate("activityGoals.plan.newProcedure")}
            </Button>
          ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          color="secondary"
          onClick={onClose}
          disabled={isSaving}
          data-testid="plan-cancel-button"
          sx={{ flex: 1 }}
        >
          {translate("activityGoals.plan.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          data-testid="plan-save-button"
          startIcon={<EventAvailable sx={{ fontSize: typography.fontSize.md }} />}
          sx={{ flex: 1, backgroundColor: color.primary, color: color.white, fontWeight: 600 }}
        >
          {translate(isSaving ? "activityGoals.plan.saving" : "activityGoals.plan.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
